import type { HubConnection, IRetryPolicy } from "@microsoft/signalr";
import { HttpTransportType, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { createSharedComposable } from "@vueuse/core";
import { secondsToMilliseconds } from "date-fns";
import { getViewerParams } from "ls/api/cloud/prototype";
import { cloudApiUrl } from "ls/api/urls";
import { useAxureCloudConfig } from "ls/state/useAxureCloudConfig";
import { joinURL } from "ufo";
import { computed, watch } from "vue";
import { testSameOrigin } from "@/common/lib";
import { retryUntilTruthy } from "@/common/lib/promise";
import { MultiSet } from "./multiset";

type HubsLogLevel = "off" | "error" | "warn" | "info" | "debug";
const hubsLogOrder: Record<HubsLogLevel, number> = { off: 0, error: 1, warn: 2, info: 3, debug: 4 };
const hubsLogLevel: HubsLogLevel = "off";

function writeLog(level: HubsLogLevel, ...args: any[]) {
  if (hubsLogLevel === "off") return;
  if (hubsLogOrder[level] > hubsLogOrder[hubsLogLevel]) return;
  const prefix = `[hubs:${level}]`;
  switch (level) {
    case "error": console.error(prefix, ...args); break;
    case "warn": console.warn(prefix, ...args); break;
    case "info": console.info(prefix, ...args); break;
    default: console.debug(prefix, ...args);
  }
}

const hubLog = {
  debug: (...a: any[]) => writeLog("debug", ...a),
  info: (...a: any[]) => writeLog("info", ...a),
  warn: (...a: any[]) => writeLog("warn", ...a),
  error: (...a: any[]) => writeLog("error", ...a),
};

export type SignalRHubListenerOf = (data: any) => unknown;
export type SignalRHubListeners = Record<string, SignalRHubListenerOf>;

export type HubName = "notifications";
export type HubGroupName = "workspace" | "folder" | "shortcut" | "shortcut-comments" | "organization";

function randomIntegerBetween(min: number, max: number) {
  const rMin = Math.ceil(min);
  const rMax = Math.floor(max);
  return Math.floor(Math.random() * (rMax - rMin)) + rMin;
}

// intervals in seconds
const intervals = [
  [0, 1],
  [2, 4],
  [5, 10],
  [20, 40],
  [60, 120],
] as const;

function retryPolicyFactory(): IRetryPolicy {
  const randomizedIntervals = intervals.map(([from, to]) => randomIntegerBetween(secondsToMilliseconds(from), secondsToMilliseconds(to)));
  const retryIntervals = [...randomizedIntervals, null];

  return {
    nextRetryDelayInMilliseconds(retryContext) {
      return retryIntervals[retryContext.previousRetryCount] ?? null;
    },
  };
}

interface HubConnectionMetadata {
  hasOnReconnectedHandler: boolean;
}

function useAxureCloudHubsConnectionFn() {
  const hubConnections = new Map<HubName, HubConnection>();
  const hubConnectionMetadata = new WeakMap<HubConnection, HubConnectionMetadata>();
  const activeHubListeners = new WeakMap<HubConnection, Set<SignalRHubListenerOf>>();

  // we're using MultiSet to track group membership counts
  // so that if multiple components subscribe to the same group
  // we only leave the group when all components unsubscribe
  const groupsMembership = new WeakMap<HubConnection, MultiSet<string>>();

  const config = useAxureCloudConfig();

  const enabled = computed(() => !!config.value?.EnablePushNotifications);

  watch(enabled, enabledValue => {
    hubLog.info("push notifications flag changed:", enabledValue);
    // for each hub connection
    for (const hubName of hubConnections.keys()) {
      const connection = hubConnections.get(hubName);
      if (!connection) continue;
      if (enabledValue) {
        ensureHubStarted(hubName).then(connection => {
          if (connection) {
            void restoreGroupsMembership(connection);
          }
        });
      } else {
        void disconnectFromHub(hubName);
      }
    }
  }, { immediate: true });

  async function awaitHubConnected(connection: HubConnection): Promise<HubConnection> {
    const state = connection.state;
    if (state === HubConnectionState.Connected) {
      return connection;
    }

    // giving some time until connection is fully ready
    const retries = 10;
    const timeout = 500;
    await retryUntilTruthy(() => connection.state === HubConnectionState.Connected, retries, timeout);

    return connection;
  }

  async function connectToHub(hubName: HubName) {
    hubLog.debug("connectToHub:", hubName);
    await ensureHubStarted(hubName);
  }

  async function disconnectFromHub(hubName: HubName) {
    const connection = hubConnections.get(hubName);
    hubLog.debug("disconnectFromHub start:", hubName, "state:", connection?.state);

    if (connection) {
      if (connection.state === HubConnectionState.Connecting) {
        // tried to immediately close connection
        // give it little timeout to finish connecting and then close connection
        await awaitHubConnected(connection);
      }
      if (connection.state === HubConnectionState.Connected) {
        await connection.stop();
      }
      hubConnections.delete(hubName);
    }
    hubLog.info("disconnectFromHub done:", hubName);
  }

  async function disconnect() {
    hubLog.info("disconnect all hubs");
    const disconnectFromHubTasks = [];
    for (const hubName of hubConnections.keys()) {
      disconnectFromHubTasks.push(disconnectFromHub(hubName));
    }
    await Promise.all(disconnectFromHubTasks);
  }

  function getConnectionMetadata(connection: HubConnection) {
    let metadata = hubConnectionMetadata.get(connection);
    if (metadata) return metadata;

    metadata = {
      hasOnReconnectedHandler: false,
    };
    hubConnectionMetadata.set(connection, metadata);
    return metadata;
  }

  async function addToGroupForConnection(connection: HubConnection, groupName: string, restoring: boolean = false) {
    hubLog.debug("addToGroupForConnection request:", groupName, "state:", connection.state);
    const groupNameNormalized = groupName.toLowerCase();
    if (connection.state === HubConnectionState.Connecting) {
      await awaitHubConnected(connection);
    }
    if (connection.state === HubConnectionState.Connected) {
      await connection.send("AddToGroup", groupNameNormalized);
    }

    if (!restoring) {
      let groups = groupsMembership.get(connection);
      if (!groups) {
        groups = new MultiSet<string>();
        groupsMembership.set(connection, groups);
      }
      groups.add(groupNameNormalized);
    }

    hubLog.info("group subscribed:", groupName);
  }

  async function restoreGroupsMembership(
    connection: HubConnection,
  ): Promise<void> {
    if (connection.state === HubConnectionState.Connected) {
      const groups = groupsMembership.get(connection);
      if (!groups) return;
      hubLog.info("restoreGroupsMembership invoked, groups count:", (groupsMembership.get(connection) && [...groupsMembership.get(connection)!].length) || 0);
      for (const group of groups) {
        await addToGroupForConnection(connection, group, true);
      }
    }
  }

  async function ensureHubStarted(hubName: HubName): Promise<HubConnection | undefined> {
    let newConnection = hubConnections.get(hubName);
    hubLog.debug("ensureHubStarted:", hubName, "existing state:", newConnection?.state);

    if (!newConnection) {
      hubLog.info("creating hub connection:", hubName);
      const hubUrl = joinURL(cloudApiUrl, "hubs", hubName);

      const isCrossOrigin = !testSameOrigin(window.location.href, hubUrl);

      newConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            if (isCrossOrigin) {
              // for cross-origin requests use vtk-based auth as cookies won't be sent
              try {
                const viewerParams = await getViewerParams();
                return viewerParams.Params.vtk ?? "";
              } catch {
                hubLog.error("failed to get vtk token for hub auth");
              }
            }

            // for same-origin requests we can use cookies-based auth
            return "";
          },
          transport: HttpTransportType.WebSockets,
          skipNegotiation: true,
        })
        .withAutomaticReconnect(retryPolicyFactory())
        .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
        .build();
      hubConnections.set(hubName, newConnection);
      activeHubListeners.set(newConnection, new Set<SignalRHubListenerOf>());
    }

    if (newConnection.state === HubConnectionState.Disconnected) {
      try {
        if (enabled.value) {
          hubLog.info("starting hub:", hubName);
          await newConnection.start();
          hubLog.info("started hub:", hubName);
        } else {
          hubLog.debug("start skipped (disabled):", hubName);
        }
      } catch (e) {
        hubLog.warn("failed to start hub:", hubName, e);
        return;
      }
    }

    await awaitHubConnected(newConnection);
    if (newConnection.state === HubConnectionState.Connected) {
      const metadata = getConnectionMetadata(newConnection);
      if (!metadata.hasOnReconnectedHandler) {
        newConnection.onreconnected(() => {
          hubLog.info("hub reconnected:", hubName, "id:", newConnection.connectionId);

          // temporarily disable group membership restoration to investigate invocation spikes
          // void restoreGroupsMembership(newConnection);
        });
        hubLog.debug("onreconnected handler attached:", hubName);
        metadata.hasOnReconnectedHandler = true;
      }
      hubLog.debug("ensureHubStarted success:", hubName);
      return newConnection;
    }

    return undefined;
  }

  async function addToGroup(hubName: HubName, groupName: string) {
    hubLog.debug("addToGroup:", hubName, groupName);
    const connection = hubConnections.get(hubName);
    if (!connection) return;
    return addToGroupForConnection(connection, groupName);
  }

  async function removeFromGroup(hubName: HubName, groupName: string) {
    hubLog.debug("removeFromGroup:", hubName, groupName);
    const groupNameNormalized = groupName.toLowerCase();
    // don't have to ensure that hub's connected
    // when client disconnects its group membership will be revoked
    const connection = hubConnections.get(hubName);

    if (connection && connection.state === HubConnectionState.Connected) {
      const groups = groupsMembership.get(connection);
      if (groups) {
        groups.delete(groupNameNormalized);
      }

      // if there are no more subscribers to this group, we can leave it
      const shouldLeave = !groups || groups.count(groupNameNormalized) === 0;
      if (shouldLeave) {
        await connection.send("RemoveFromGroup", groupNameNormalized);
      }
    }
    hubLog.info("removeFromGroup processed:", hubName, groupName);
  }

  async function addHubListeners(hubName: HubName, listeners: SignalRHubListeners) {
    hubLog.debug("addHubListeners:", hubName, Object.keys(listeners));
    const connection = await ensureHubStarted(hubName);
    if (connection) {
      for (const methodName in listeners) {
        if (Object.prototype.hasOwnProperty.call(listeners, methodName)) {
          const listener = listeners[methodName];
          if (!listener) continue;

          connection.on(methodName, listener);
          const hubListeners = activeHubListeners.get(connection);
          if (hubListeners) {
            hubListeners.add(listener);
          }
        }
      }
    }
    hubLog.info("listeners added:", hubName);
  }

  async function removeHubListeners(hubName: HubName, listeners: SignalRHubListeners) {
    hubLog.debug("removeHubListeners:", hubName, Object.keys(listeners));
    const connection = hubConnections.get(hubName);
    if (connection) {
      for (const methodName in listeners) {
        if (Object.prototype.hasOwnProperty.call(listeners, methodName)) {
          const listener = listeners[methodName];
          if (!listener) continue;

          connection.off(methodName, listener);
          const hubListeners = activeHubListeners.get(connection);
          if (hubListeners) {
            hubListeners.delete(listener);
          }
        }
      }

      const hubListeners = activeHubListeners.get(connection);
      if (!hubListeners || hubListeners.size === 0) {
        await disconnectFromHub(hubName);
      }
    }
    hubLog.info("listeners removed:", hubName, "remaining:", hubConnections.get(hubName) ? activeHubListeners.get(hubConnections.get(hubName)!)?.size : 0);
  }

  return {
    connectToHub,
    disconnect,
    addHubListeners,
    removeHubListeners,
    addToGroup,
    removeFromGroup,
  };
}

export const useAxureCloudHubsConnection = createSharedComposable(useAxureCloudHubsConnectionFn);
